/**
 * ============================================================================
 * useAutoOpenFromQuery — Abre modal automaticamente via `?new=<value>`
 * ----------------------------------------------------------------------------
 * Consumido pelas páginas destino do botão "Novo" (Quick Create) do header.
 * Ao detectar o valor esperado no query string, dispara o `onMatch` uma única
 * vez e remove o parâmetro da URL (mantendo demais params intactos).
 *
 * RELEASE: 2026-08-06 v1.1.0 - Permite reabrir o modal quando o parâmetro
 *          `new` reaparece na URL (atalho acionado 2x na mesma página).
 * RELEASE: 2026-07-24 v1.0.0 - Criação.
 * ============================================================================
 */
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * @param expectedValue Valor esperado no parâmetro `new` (ex.: '1', 'chat').
 * @param onMatch Callback disparado quando o valor bate (geralmente abre modal).
 * @param enabled Guard opcional — não abre até que dependências estejam prontas.
 */
export function useAutoOpenFromQuery(
  expectedValue: string,
  onMatch: () => void,
  enabled: boolean = true,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const firedRef = useRef(false);

  useEffect(() => {
    const current = searchParams.get('new');

    // v1.1.0: sem o parâmetro na URL, libera novo disparo futuro.
    if (current !== expectedValue) {
      if (current === null) firedRef.current = false;
      return;
    }

    if (!enabled || firedRef.current) return;

    firedRef.current = true;
    onMatch();

    // Remove o parâmetro `new` mantendo os demais.
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, searchParams, expectedValue]);
}

